import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import Box from '@mui/material/Box';
import { Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel } from '@mui/material';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import MobileDrawer from 'feature/dispatching/components/MobileDrawer';
import LocationHeader from './LocationHeader';
import LoadCard from './LoadCard';
import LoadCardSkeleton from './LoadCardSkeleton';
import LoadRow from './LoadRow';
import FilterTabs from './FilterTabs';
import FiltersByLocation from './FiltersByLocation';
import { useViewLoadsDetailsByLocationQuery } from '../loadsEndpoints';

const containerStyles = {
  /* eslint-disable-next-line */
  bgcolor: 'surface.light',
  mt: { md: 6, xs: 0 },
  pb: { md: 0, xs: 8 },
  pl: { md: 37.5, xs: 1 },
  pr: { md: 0, xs: 1 },
};

const filterFormats = createdDate => {
  const d = new Date(createdDate);
  return `
    ${createdDate}
    ${format(d, 'MMMM dd, yyyy')}
    ${format(d, 'MM/dd/yyyy')}
    ${format(d, 'M/dd/yyyy')}
    ${format(d, 'MM/d/yyyy')}
    ${format(d, 'M/d/yyyy')}
  `;
};

const initialCardsFilters = {
  createdDate: '',
  driver: '',
  poNumber: '',
  status: 'All',
  vaporTightnessCode: '',
};

const ViewLoadsDetailsByLocation = ({
  onCreate,
  onDelete,
  onEdit,
  onDuplicate,
  onView,
  onFavorite,
  onUnfavorite,
}) => {
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });
  const { viewById } = useParams();
  const viewByLocationId = parseInt(viewById);

  const [layout, setLayout] = useState('grid');
  const [sortColumn, setSortColumn] = useState({
    direction: 'asc',
    field: 'location.name',
  });
  const [tabFilter, setTabFilter] = useState('ALL');
  const [cardsFilters, setCardsFilters] = useState(initialCardsFilters);

  const {
    data: allLoads = [],
    isLoading,
    isSuccess,
  } = useViewLoadsDetailsByLocationQuery(
    { locationId: viewByLocationId },
    { skip: R.isNil(viewById) }
  );

  const isSubmitted = R.anyPass([
    R.pathEq(['load', 'status'], 'PENDING'),
    R.pathEq(['load', 'status'], 'SCHEDULED'),
    R.pathEq(['load', 'status'], 'WARNING'),
  ]);
  const submittedLoads = allLoads.filter(isSubmitted);
  const draftLoads = allLoads.filter(R.pathEq(['load', 'status'], 'DRAFT'));
  const favoriteLoads = allLoads.filter(R.pathEq(['load', 'isFavorite'], true));

  const isGridView = layout === 'grid' || !lgUp;

  const boxStyles = isGridView
    ? {
        display: 'grid',
        gap: 3,
        gridTemplateColumns: `repeat(auto-fit, 300px)`,
        ml: 3,
      }
    : { ml: 3 };

  const appliedFilters = [
    R.isEmpty(cardsFilters.driver)
      ? null
      : {
          clearValue: '',
          field: 'driver',
          label: 'Driver',
          value: cardsFilters.driver,
        },
    R.equals(cardsFilters.status, 'All')
      ? null
      : {
          clearValue: 'All',
          field: 'status',
          label: 'Status',
          value: cardsFilters.status,
        },
    R.isEmpty(cardsFilters.createdDate)
      ? null
      : {
          clearValue: '',
          field: 'createdDate',
          label: 'Date Created',
          value: cardsFilters.createdDate,
        },
    R.isEmpty(cardsFilters.vaporTightnessCode)
      ? null
      : {
          clearValue: '',
          field: 'vaporTightnessCode',
          label: 'Trailer ID',
          value: cardsFilters.vaporTightnessCode,
        },
    R.isEmpty(cardsFilters.poNumber)
      ? null
      : {
          clearValue: '',
          field: 'poNumber',
          label: 'PO#',
          value: cardsFilters.poNumber,
        },
  ].filter(Boolean);

  const tabs = [
    {
      count: allLoads.length,
      id: 'ALL',
      name: 'All Loads',
      shortName: 'All',
    },
    {
      count: submittedLoads.length,
      id: 'SUBMITTED',
      name: 'Submitted Loads',
      shortName: 'Submitted',
    },
    {
      count: draftLoads.length,
      id: 'DRAFT',
      name: 'Draft Loads',
      shortName: 'Draft',
    },
    {
      count: favoriteLoads.length,
      id: 'FAVORITE',
      name: 'Favorite Loads',
      shortName: 'Favorites',
    },
  ];

  const handleTabChange = R.compose(setTabFilter, R.nthArg(1));

  // prettier-ignore
  const handleSort = field => setSortColumn(column => {
    const direction = R.equals(field, column.field)
      ? (R.equals(column.direction, 'asc') ? 'desc' : 'asc') 
      : 'asc';
    return { direction, field };
  });

  const handleClearFilters = () => {
    setCardsFilters(initialCardsFilters);
  };

  const driverFilter = R.compose(
    R.includes(cardsFilters.driver.toUpperCase()),
    R.toUpper,
    ({ firstName, lastName }) => `${lastName}, ${firstName}`,
    R.prop('driver')
  );

  // prettier-ignore
  const statusFilter = R.compose(
    R.either(
      R.always(R.equals(cardsFilters.status, 'All')),
      R.equals(R.toUpper(cardsFilters.status))
    ),
    R.path(['load', 'status'])
  );

  const createdDateFilter = R.compose(
    R.includes(R.toUpper(cardsFilters.createdDate)),
    R.toUpper,
    filterFormats,
    R.path(['load', 'createdDate'])
  );

  const vaporTightnessCodeFilter = R.compose(
    R.includes(cardsFilters.vaporTightnessCode.toUpperCase()),
    R.toUpper,
    R.path(['trailer', 'vaporTightnessCode'])
  );

  const poNumberFilter = R.compose(
    R.includes(cardsFilters.poNumber.toUpperCase()),
    R.toUpper,
    R.path(['load', 'poNumber'])
  );

  const scend = R.ifElse(
    R.equals('asc'),
    R.always(R.ascend),
    R.always(R.descend)
  )(sortColumn.direction);

  // prettier-ignore
  const sortComparator = column => scend(
    R.compose(
      R.when(R.is(String), R.toUpper), 
      R.path(R.split('.', column.field))
    )
  );

  const filteredData = R.cond([
    [R.equals('ALL'), R.always(allLoads)],
    [R.equals('SUBMITTED'), R.always(submittedLoads)],
    [R.equals('DRAFT'), R.always(draftLoads)],
    [R.equals('FAVORITE'), R.always(favoriteLoads)],
  ])(tabFilter)
    .filter(driverFilter)
    .filter(statusFilter)
    .filter(createdDateFilter)
    .filter(vaporTightnessCodeFilter)
    .filter(poNumberFilter)
    .sort(sortComparator(sortColumn));

  const filtersPanel = (
    <FiltersByLocation
      appliedFilters={appliedFilters}
      cardsFilters={cardsFilters}
      onClearFilters={handleClearFilters}
      setCardsFilters={setCardsFilters}
    />
  );

  const renderContent = () => (
    <Box sx={boxStyles}>
      {isLoading && (
        <>
          <LoadCardSkeleton />
          <LoadCardSkeleton />
          <LoadCardSkeleton />
        </>
      )}

      {isSuccess &&
        isGridView &&
        filteredData.map(({ load, driver, trailer }) => (
          <LoadCard
            driver={driver}
            key={load.loadId}
            load={load}
            onDelete={onDelete}
            onDuplicate={onDuplicate}
            onEdit={onEdit}
            onFavorite={onFavorite}
            onUnfavorite={onUnfavorite}
            onView={onView}
            subheader={trailer.vaporTightnessCode}
            subtitle={trailer.vaporTightnessCode}
            title={`${R.toUpper(driver.lastName)}, ${R.toUpper(driver.firstName)}`}
          />
        ))}

      {isSuccess && !isGridView && (
        <Table sx={{ borderCollapse: 'separate', borderSpacing: '0 16px', width: '100%' }}>
          <TableHead
            sx={{
              bgcolor: 'surface.light',
              height: 76,
              position: 'sticky',
              top: '190px',
              zIndex: 99,
            }}
          >
            <TableRow>
              <HeaderCell
                field="driver.lastName"
                label="Driver"
                onSort={handleSort}
                sortColumn={sortColumn}
              />
              <HeaderCell
                field="trailer.vaporTightnessCode"
                label="Trailer ID"
                onSort={handleSort}
                sortColumn={sortColumn}
              />
              <HeaderCell
                field="load.poNumber"
                label="PO#"
                onSort={handleSort}
                sortColumn={sortColumn}
              />
              <HeaderCell
                field="load.status"
                label="Status"
                onSort={handleSort}
                sortColumn={sortColumn}
              />
              <HeaderCell
                field="load.createdDate"
                label="Created"
                onSort={handleSort}
                sortColumn={sortColumn}
              />
              <HeaderCell
                field="load.updatedDate"
                label="Last Edited"
                onSort={handleSort}
                sortColumn={sortColumn}
              />
              <HeaderCell
                field="placeholder"
                label=""
                onSort={handleSort}
                sortColumn={sortColumn}
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.map(({ load, driver, trailer }) => (
              <LoadRow
                driver={driver}
                key={load.loadId}
                load={load}
                onDelete={onDelete}
                onDuplicate={onDuplicate}
                onEdit={onEdit}
                onFavorite={onFavorite}
                onUnfavorite={onUnfavorite}
                onView={onView}
                subtitle={trailer.vaporTightnessCode}
                title={`${R.toUpper(driver.lastName)}, ${R.toUpper(driver.firstName)}`}
              />
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );

  return lgUp ? (
    <Box sx={containerStyles}>
      <LocationHeader
        appliedFilters={appliedFilters}
        filters={cardsFilters}
        filtersPanel={filtersPanel}
        layout={layout}
        onCreate={onCreate}
        onLayoutChange={setLayout}
        setFilters={setCardsFilters}
      >
        <FilterTabs
          activeTab={tabFilter}
          isLoading={isLoading}
          onChange={handleTabChange}
          tabs={tabs}
        />
      </LocationHeader>
      {renderContent()}
    </Box>
  ) : (
    <MobileDrawer
      backLabel="Back to Locations"
      createLabel="New Load"
      filtersPanel={filtersPanel}
      module="loads"
      onCreate={onCreate}
      open={!!viewById}
      setTitle={() => {}}
    >
      {renderContent()}
      <FilterTabs
        activeTab={tabFilter}
        isLoading={isLoading}
        onChange={handleTabChange}
        tabs={tabs}
      />
    </MobileDrawer>
  );
};

ViewLoadsDetailsByLocation.propTypes = {
  onCreate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onFavorite: PropTypes.func.isRequired,
  onUnfavorite: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
};

export default ViewLoadsDetailsByLocation;

const HeaderCell = ({ label, field, sortColumn, onSort }) => (
  <TableCell sx={{ borderBottom: 0, px: 1, py: 0, typography: 'subtitle2' }}>
    <TableSortLabel
      active={R.equals(sortColumn.field, field)}
      direction={sortColumn.direction}
      onClick={() => onSort(field)}
    >
      <Typography noWrap>{label}</Typography>
    </TableSortLabel>
  </TableCell>
);
HeaderCell.propTypes = {
  field: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  onSort: PropTypes.func.isRequired,
  sortColumn: PropTypes.shape({
    direction: PropTypes.string,
    field: PropTypes.string,
  }).isRequired,
};
